import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api, StorefrontWithStats } from '../api';
import Layout from '../components/Layout';

function formatCurrency(amount: number, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
}

export default function AdminDashboard() {
  const [storefronts, setStorefronts] = useState<StorefrontWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadStorefronts();
  }, []);

  const loadStorefronts = async () => {
    try {
      const { storefronts } = await api.getStorefronts();
      setStorefronts(storefronts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Stats summary
  const totalStorefronts = storefronts.length;
  const activeStorefronts = storefronts.filter((s) => s.status === 'active').length;
  const totalOrders = storefronts.reduce((sum, s) => sum + s.stats.totalOrders, 0);
  const totalRevenue = storefronts.reduce((sum, s) => sum + s.stats.totalRevenue, 0);

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-ink-50">Admin Dashboard</h1>
            <p className="text-ink-400 mt-1">Overview of all influencer storefronts</p>
          </div>
          <Link to="/admin/influencers" className="btn-primary">
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            Manage Influencers
          </Link>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="stat-card animate-slide-up stagger-1">
            <p className="text-ink-400 text-sm mb-1">Total Influencers</p>
            <p className="text-3xl font-display font-bold text-ink-50">{totalStorefronts}</p>
          </div>
          <div className="stat-card animate-slide-up stagger-2">
            <p className="text-ink-400 text-sm mb-1">Active</p>
            <p className="text-3xl font-display font-bold text-mint-400">{activeStorefronts}</p>
          </div>
          <div className="stat-card animate-slide-up stagger-3">
            <p className="text-ink-400 text-sm mb-1">Total Orders</p>
            <p className="text-3xl font-display font-bold text-ink-50">{totalOrders.toLocaleString()}</p>
          </div>
          <div className="stat-card animate-slide-up stagger-4">
            <p className="text-ink-400 text-sm mb-1">Total Revenue</p>
            <p className="text-3xl font-display font-bold text-accent-400">{formatCurrency(totalRevenue)}</p>
          </div>
        </div>

        {/* Error display */}
        {error && (
          <div className="p-4 rounded-lg bg-accent-500/10 border border-accent-500/20 text-accent-400">
            {error}
          </div>
        )}

        {/* Storefronts Table */}
        <div className="table-container animate-slide-up stagger-5">
          <div className="table-header p-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-ink-50">Influencer Storefronts</h2>
            <Link to="/admin/influencers" className="text-sm text-accent-400 hover:text-accent-300">
              View All →
            </Link>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-4 border-accent-500/30 border-t-accent-500 rounded-full animate-spin mx-auto" />
            </div>
          ) : storefronts.length === 0 ? (
            <div className="p-8 text-center text-ink-400">
              <p className="mb-4">No influencers yet.</p>
              <Link to="/admin/influencers" className="btn-primary">
                Add Your First Influencer
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-ink-400 text-sm">
                    <th className="px-4 py-3 font-medium">Storefront</th>
                    <th className="px-4 py-3 font-medium">ID</th>
                    <th className="px-4 py-3 font-medium">Orders</th>
                    <th className="px-4 py-3 font-medium">Revenue</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {storefronts.slice(0, 10).map((sf) => (
                    <tr key={sf.id} className="table-row">
                      <td className="px-4 py-4">
                        <div>
                          <p className="font-medium text-ink-100">{sf.display_name}</p>
                          <p className="text-sm text-ink-500">{sf.storefront_slug}</p>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <code className="text-xs bg-ink-800 px-2 py-1 rounded font-mono">
                          {sf.storefront_id}
                        </code>
                      </td>
                      <td className="px-4 py-4 text-ink-200">
                        {sf.stats.totalOrders.toLocaleString()}
                      </td>
                      <td className="px-4 py-4 text-ink-200">
                        {formatCurrency(sf.stats.totalRevenue)}
                      </td>
                      <td className="px-4 py-4">
                        <span className={sf.status === 'active' ? 'badge-success' : 'badge-warning'}>
                          {sf.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Quick Links */}
        <div className="grid md:grid-cols-2 gap-4">
          <Link
            to="/admin/influencers"
            className="card p-6 hover:bg-ink-800/50 transition-colors group"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-accent-500/10 flex items-center justify-center group-hover:bg-accent-500/20 transition-colors">
                <svg className="w-6 h-6 text-accent-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-ink-100">Manage Influencers</h3>
                <p className="text-sm text-ink-400">Add, edit, and manage influencer accounts</p>
              </div>
            </div>
          </Link>

          <div className="card p-6 opacity-50">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-ink-700/50 flex items-center justify-center">
                <svg className="w-6 h-6 text-ink-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-ink-300">Review Orders</h3>
                <p className="text-sm text-ink-500">Coming soon: Review unattributed orders</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
